import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { catchError, mergeMap, Observable, of, throwError } from 'rxjs';

import { EcmDispatcherModule } from '@ecm/dispatcher';
import { PortfolioService, UrlHelper, Usage, UsageHelper } from '@ecm/portfolio';

import { ErrorPageService } from '../../services/error-page.service';
import { RouterService } from '../../services/router.service';
import { AbstractPortfolioResolver } from './shared/abstract-portfolio.resolver';

@Injectable({
    providedIn: 'root',
})
export class UsagePortfolioResolver extends AbstractPortfolioResolver {
    /**
     * Usage selector routing path
     * @type {string}
     */
    private static readonly USAGE_SELECTOR_PATH: string = 'selectionner-un-usage';

    private routeContractId: string;

    /**
     * Is usage owned by contract
     * @param {string} usage
     * @param {Usage[]} contractUsages
     * @returns {boolean}
     */
    private static isUsageOwnedByContract(usage: string, contractUsages: Usage[]): boolean {
        return (
            contractUsages.length > 0 &&
            contractUsages.some((contractUsage: Usage) => contractUsage.usageCode === usage)
        );
    }

    /**
     * Is usage authorized on route
     * @param {string} usage
     * @param {string[]} authorizedUsages
     * @returns {boolean}
     */
    private static isUsageAuthorizedOnRoute(usage: string, authorizedUsages: string[]): boolean {
        return (
            authorizedUsages &&
            authorizedUsages.some(
                (authorizedUsage: string) => UsageHelper.extractUsageCodeFromUsage(usage) === authorizedUsage,
            )
        );
    }

    public constructor(
        protected errorPageService: ErrorPageService,
        protected portfolioService: PortfolioService,
        protected routerService: RouterService,
    ) {
        super(errorPageService, portfolioService, routerService);
    }

    /**
     * Checks usage consistency on current route.
     * On invalid or missing usage user will redirect to UsageComponent to select one
     * @param {ActivatedRouteSnapshot} activatedRouteSnapshot
     * @param {RouterStateSnapshot} routerStateSnapshot
     * @returns {Observable<boolean>}
     */
    public resolve(
        activatedRouteSnapshot: ActivatedRouteSnapshot,
        routerStateSnapshot: RouterStateSnapshot,
    ): Observable<boolean> {
        if (!activatedRouteSnapshot?.data?.authorizedUsage) {
            EcmDispatcherModule.sendConsoleError(
                UsagePortfolioResolver.name,
                `Authorized usage list is not provided on route ${routerStateSnapshot.url}`,
            );

            this.routerService.navigateToHome();
            return of(false);
        }

        // list of available usage on route
        const routeAuthorizedUsage: string[] = activatedRouteSnapshot.data.authorizedUsage;

        return this.checkContract(activatedRouteSnapshot, routerStateSnapshot).pipe(
            mergeMap(() => {
                this.routeContractId = UrlHelper.matchCid(routerStateSnapshot.url)[1];
                const routeUsage: string = activatedRouteSnapshot.params.usage;

                if (
                    UsageHelper.isUsageWellFormatted(routeUsage) &&
                    UsagePortfolioResolver.isUsageAuthorizedOnRoute(routeUsage, routeAuthorizedUsage)
                ) {
                    return this.portfolioService.getUsagesFromContractCid(this.routeContractId).pipe(
                        mergeMap((usages: Usage[]) => {
                            if (UsagePortfolioResolver.isUsageOwnedByContract(routeUsage, usages)) {
                                EcmDispatcherModule.sendConsoleInfo(
                                    UsagePortfolioResolver.name,
                                    `Usage ${routeUsage} matches portfolio, will let go to route ${routerStateSnapshot.url}`,
                                );

                                return of(true);
                            } else {
                                return throwError(
                                    () => `Usage ${routeUsage} is not owned by contract ${this.routeContractId}`,
                                );
                            }
                        }),
                    );
                } else {
                    return throwError(() => `Usage ${routeUsage} is not valid or not authorized on route`);
                }
            }),
            catchError((error: any) => {
                EcmDispatcherModule.sendConsoleError(UsagePortfolioResolver.name, error);

                let returnUrl: string = routerStateSnapshot.url;

                // if provided an incorrect contract or usage, replace it by the placeholder
                if (activatedRouteSnapshot?.params?.cid && activatedRouteSnapshot?.params?.usage) {
                    returnUrl = routerStateSnapshot.url
                        // use previous contract id if valid to act as filter on usage selector
                        .replace(activatedRouteSnapshot.params.cid, this.routeContractId ?? encodeURI('{{cid}}'))
                        .replace(activatedRouteSnapshot.params.usage, encodeURI('{{usage}}'));
                }

                // let's go to usage selector page
                this.routerService.navigate([UsagePortfolioResolver.USAGE_SELECTOR_PATH], {
                    queryParams: { returnUrl, authorizedUsage: routeAuthorizedUsage },
                });

                return of(false);
            }),
        );
    }
}
